import { ContactLink, L, LegalPageShell, P, type LegalDocument } from './LegalPage';

/**
 * LegalDemoNotice — /legal/demo
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAINTAINERS: PRE-COUNSEL DRAFT. Replace `DEMO_NOTICE` wholesale.
 *
 * This is the demonstration-environment disclaimer: what this instance is, what
 * it is not, and what a reader must not rely on. It is NOT a terms of service —
 * it deliberately grants no licence, sets no fees, and disclaims no liability,
 * because doing any of those badly is worse than not doing them here. A signed
 * agreement replaces it before production use.
 *
 * Everything asserted below is true of the deployed code as of Aug 2026:
 *   • the environment is reset/re-seeded by scripts/seed-demo.js and migrations
 *   • no automated retention, backup or deletion schedule exists
 *   • the calculation engine's own reference data carries an effective date that
 *     is not the same thing as "current" (see StateRulesDisclaimer)
 * Nothing here promises uptime, support, retention or accuracy, because none of
 * those are implemented or contracted.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const DEMO_NOTICE: LegalDocument = {
  title: 'Demonstration environment notice',
  summary:
    'What this instance of Ignite is, and what you should not rely on it for.',
  notice: (
    <>
      <strong style={{ display: 'block', marginBottom: 2 }}>
        This is a demonstration instance, not a production service.
      </strong>
      Data in it may be changed, reset or deleted at any time without notice, and nothing
      here is a contract. Do not use it as a system of record, and do not act on a figure
      or a deadline you read here without checking it against the source.
    </>
  ),
  intro: (
    <P>
      Ignite is property-tax appeal management software. The instance you are using is
      set up to demonstrate and evaluate that software. This page explains the limits of
      that arrangement in plain terms so nobody is surprised by them later. A companion{' '}
      <a href="/legal/privacy" style={{ color: 'var(--color-hunter)' }}>privacy notice</a>{' '}
      covers what the software does with information.
    </P>
  ),
  sections: [
    {
      heading: 'This environment is for demonstration',
      body: (
        <>
          <P>
            The instance exists so that firms, their clients and our own team can see the
            software working with realistic data. It is not a live production deployment,
            and it is not covered by a service agreement.
          </P>
          <L items={[
            'It may be reset, re-seeded, migrated, taken offline or rebuilt at any time, without notice and without a backup you can ask us to restore.',
            'Records you create may not survive the next deployment. Treat anything you enter as temporary.',
            'There is no uptime commitment, no support commitment and no response-time commitment.',
            'Features may appear, change or disappear between sessions.',
          ]} />
        </>
      ),
    },
    {
      heading: 'Do not put anything here you cannot afford to lose or share',
      body: (
        <>
          <P>
            The instance carries a mixture of sample data and real records loaded for
            demonstration purposes. Access is shared across a demonstration audience, and
            the environment is not segregated the way a production deployment would be.
          </P>
          <L items={[
            'Upload only what you are authorised to share, and only what you could stand to lose.',
            'Do not upload anything you would not be comfortable having removed in a reset.',
            'If you are demonstrating with client documents, consider whether redacted or sample versions would do the job.',
          ]} />
        </>
      ),
    },
    {
      heading: 'Nothing here is legal, tax or valuation advice',
      body: (
        <>
          <P>
            Ignite organises property-tax appeal work. It does not perform it, and it
            does not replace the judgement of the professionals who do.
          </P>
          <L items={[
            'Assessed values, savings estimates, fee and commission figures, tax bills and invoices shown in the product are working outputs of a calculation engine, not authoritative amounts.',
            'Appeal windows, assessment ratios, forms and procedures held in the product are a working aid. Statutes change and an individual jurisdiction can differ from its state’s general rule.',
            'Confirm anything that affects a filing, a deadline or a client’s money with the taxing authority, or with your own counsel, before you act on it.',
          ]} />
          <P>
            Where the product knows a figure is reference data rather than fact, it says so
            on the page. Those on-page notes are the ones that matter most — please read
            them.
          </P>
        </>
      ),
    },
    {
      heading: 'These are not terms of service',
      body: (
        <>
          <P>
            This page is a notice, not an agreement. It does not grant you a licence to the
            software, set fees, allocate liability or create any commitment on either side.
          </P>
          <P>
            Before Ignite is used in production for real client work, a written
            agreement — reviewed by counsel — will govern the relationship, and a
            counsel-reviewed privacy policy will replace the notice linked below. If you
            need those documents in order to evaluate the product, ask and we will tell you
            where they stand.
          </P>
        </>
      ),
    },
    {
      heading: 'Access and accounts',
      body: (
        <>
          <P>
            Accounts in this environment, including demonstration client-portal logins, are
            issued for evaluation. Some of them use shared or published credentials; the
            sign-in page shows demonstration client logins on screen when the instance is
            running in demonstration mode. That is intentional, and it is another reason to
            treat everything in the instance as visible to the demonstration audience.
          </P>
          <P>
            Please do not share access outside the people the demonstration is for, and tell
            us if you think an account should be withdrawn.
          </P>
        </>
      ),
    },
    {
      heading: 'Questions',
      body: (
        <P>
          If you were given access by a property-tax consulting firm, that firm is your first
          point of contact. For anything about the environment itself, write to{' '}
          <ContactLink />.
        </P>
      ),
    },
  ],
};

export function LegalDemoNotice() {
  return (
    <LegalPageShell
      doc={DEMO_NOTICE}
      otherHref="/legal/privacy"
      otherLabel="Privacy notice"
    />
  );
}
